import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { motion, AnimatePresence, useSpring, useTransform } from "framer-motion";
import { Activity, Settings2, Check, X, TrendingDown, Cpu, RefreshCw, Loader2, Wifi, WifiOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { useTheme } from "../../contexts/ThemeContext";
import { toast } from "sonner";
import { CylinderMimic } from "./CylinderMimic";
import { API_BASE } from '../../utils/api-base';
import { getWebSocketClient } from '../../utils/websocket-client';
import { AreaChart, Area, ResponsiveContainer, Tooltip, YAxis } from "recharts";

interface ScannerDashboardProps {
  clientId: string;
  token: string;
  permissions: any;
  pollingIntervalMs?: number;
}

interface ScalingForm {
  scaling: boolean;
  rawMin: string;
  rawMax: string;
  engMin: string;
  engMax: string;
  unit: string;
  inputType: number;
  unitIndex: number;
}

// Common units for dropdown

// Convert address to channel name
const getChannelName = (address: string | number) => {
  const addr = parseInt(String(address));
  // Addresses 3-18 are channels 1-16
  if (addr >= 3 && addr <= 18) {
    return `Channel ${addr - 2}`;
  }
  return `Address ${addr}`;
};

const INPUT_TYPES = [
  { value: 1, label: "J TYPE" },
  { value: 2, label: "K" },
  { value: 3, label: "T" },
  { value: 4, label: "R" },
  { value: 5, label: "S" },
  { value: 6, label: "B" },
  { value: 7, label: "N" },
  { value: 9, label: "RTD" },
  { value: 11, label: "MA (4-20 mA)" },
  { value: 16, label: "V (0-10V)" },
];

const UNIT_TYPES = [
  { value: 1, label: "°C" },
  { value: 9, label: "bar" },
  { value: 11, label: "PSI" },
  { value: 12, label: "kg/cm²" },
  { value: 13, label: "mmWG" },
  { value: 15, label: "mmHg" },
  { value: 25, label: "mA" },
];

const getAuthHeaders = (token: string, headers: Record<string, string> = {}) => {
  return token ? { ...headers, Authorization: `Bearer ${token}` } : headers;
};

const readApiError = async (response: Response, fallback: string) => {
  try {
    const payload = await response.json();
    return payload?.error || payload?.message || fallback;
  } catch {
    return fallback;
  }
};

function AnimatedNumber({ value, decimals }: { value: number, decimals: number }) {
  const spring = useSpring(value, { bounce: 0, duration: 800 });

  useEffect(() => {
    spring.set(value);
  }, [value, spring]);

  const display = useTransform(spring, (current) => current.toFixed(decimals));

  return <motion.span>{display}</motion.span>;
}

function IndicatorCirclesRow({ indicators, pct, hasValue, dk, tagId, showSettings, onIndicatorsChange }: any) {
  const [editingId, setEditingId] = useState<string | null>(null);

  // Always ensure exactly 6 indicators exist with defaults
  const DEFAULT_INDICATORS = [
    { id: "1", value: 16.67, color: "#10b981" },
    { id: "2", value: 33.33, color: "#10b981" },
    { id: "3", value: 50.00, color: "#f59e0b" },
    { id: "4", value: 66.67, color: "#f59e0b" },
    { id: "5", value: 83.33, color: "#ef4444" },
    { id: "6", value: 100.0, color: "#ef4444" },
  ];

  // Merge saved indicators into the 6 fixed slots by index
  const sixInds = DEFAULT_INDICATORS.map((def, i) => {
    const saved = [...indicators].sort((a: any, b: any) => a.value - b.value)[i];
    return saved ? { ...def, ...saved, id: def.id } : def;
  });

  const updateInd = (id: string, updates: any) => {
    const next = sixInds.map((i: any) => i.id === id ? { ...i, ...updates } : i);
    onIndicatorsChange(next);
  };

  return (
    <div className="mt-3 pt-2 border-t border-dashed border-slate-300/30 dark:border-slate-600/30">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-[10px] font-bold uppercase tracking-wider ${dk ? "text-slate-400" : "text-slate-500"}`}>
          Level Indicators
        </span>
        <span className={`text-[10px] font-black ${dk ? "text-emerald-400" : "text-emerald-600"}`}>
          {hasValue ? `${pct.toFixed(1)}%` : "—"}
        </span>
      </div>
      <div className="flex items-center gap-3">
        {sixInds.map((ind: any, index: number) => {
          const isActive = hasValue && (pct >= ind.value);
          const activeShadow = `0 0 10px ${ind.color}CC`;
          const isEditing = editingId === ind.id;
          return (
            <div key={ind.id} className="relative">
              {/* Circle — always clickable to open settings */}
              <motion.div
                animate={{ scale: isActive ? [1, 1.15, 1] : 1, opacity: isActive ? 1 : 0.5 }}
                transition={{ duration: 0.35 }}
                onClick={() => setEditingId(isEditing ? null : ind.id)}
                className={`w-5 h-5 rounded-full cursor-pointer transition-all duration-300 ring-2 ring-offset-1 ${
                  isEditing ? "ring-blue-400 ring-offset-white dark:ring-offset-slate-900" : "ring-transparent hover:ring-blue-300"
                } ${isActive ? "" : (dk ? "border-2 border-slate-500/60" : "border-2 border-slate-300")}`}
                style={{
                  backgroundColor: isActive ? ind.color : (dk ? "rgba(51,65,85,0.6)" : "rgba(226,232,240,1)"),
                  boxShadow: isActive ? activeShadow : "none",
                }}
                title={`Circle ${index + 1}: triggers at ${ind.value}%`}
              />

              {/* Per-circle settings popup */}
              <AnimatePresence>
                {isEditing && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.88, y: 6 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.88, y: 6 }}
                    transition={{ duration: 0.15 }}
                    className={`absolute top-8 z-40 w-40 rounded-xl shadow-2xl border p-3 flex flex-col gap-2.5 ${
                      dk ? "bg-slate-800 border-slate-600" : "bg-white border-slate-200 drop-shadow-xl"
                    }`}
                    style={{ left: index >= 4 ? "auto" : "50%", right: index >= 4 ? 0 : "auto", transform: index >= 4 ? "none" : "translateX(-50%)" }}
                  >
                    {/* Pointer arrow */}
                    <div className="absolute -top-[7px] left-1/2 -translate-x-1/2 w-3.5 h-3.5 rotate-45 border-l border-t rounded-tl-sm"
                      style={{ background: dk ? "#1e293b" : "#ffffff", borderColor: dk ? "#475569" : "#e2e8f0" }}
                    />

                    {/* Circle label */}
                    <div className="flex items-center gap-2 mb-0.5">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: ind.color }} />
                      <span className={`text-[11px] font-bold ${dk ? "text-white" : "text-slate-700"}`}>
                        Indicator {index + 1}
                      </span>
                    </div>

                    {/* Trigger % */}
                    <div>
                      <label className={`text-[9px] font-bold uppercase tracking-widest block mb-1 ${
                        dk ? "text-slate-400" : "text-slate-500"
                      }`}>Set Trigger %</label>
                      <input
                        type="number"
                        min={0} max={100} step={1}
                        value={ind.value}
                        onChange={(e) => updateInd(ind.id, { value: parseFloat(e.target.value) || 0 })}
                        className={`w-full h-8 px-2 text-sm font-bold rounded-lg border-2 focus:outline-none focus:border-blue-400 transition-colors ${
                          dk
                            ? "bg-slate-700 border-slate-600 text-white focus:bg-slate-600"
                            : "bg-slate-50 border-slate-200 text-slate-800 focus:bg-white"
                        }`}
                      />
                    </div>

                    {/* Color */}
                    <div>
                      <label className={`text-[9px] font-bold uppercase tracking-widest block mb-1 ${
                        dk ? "text-slate-400" : "text-slate-500"
                      }`}>Color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={ind.color}
                          onChange={(e) => updateInd(ind.id, { color: e.target.value })}
                          className="w-9 h-8 rounded-lg cursor-pointer border border-slate-300 p-0.5"
                        />
                        <span className={`text-[10px] font-mono font-bold ${dk ? "text-slate-300" : "text-slate-600"}`}>{ind.color}</span>
                      </div>
                    </div>

                    {/* Close */}
                    <button
                      onClick={() => setEditingId(null)}
                      className={`flex items-center justify-center gap-1 w-full py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${
                        dk ? "bg-slate-700 hover:bg-slate-600 text-slate-300" : "bg-slate-100 hover:bg-slate-200 text-slate-600"
                      }`}
                    >
                      <Check className="w-3 h-3" /> Done
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TagCard({ tag, plcId, onRefresh, theme, userRole, token, history }: { tag: any; plcId: string; onRefresh: () => void; theme: string; userRole?: string; token: string; history: { time: string; value: any }[] }) {
  const [showConfig, setShowConfig] = useState(false);
  const [saving, setSaving] = useState(false);
  const normalizeTagForm = (source: any): ScalingForm => ({
    scaling: source.scaling === true || source.scaling === 1,
    rawMin: String(source.rawMin ?? source.raw_min ?? "6554"),
    rawMax: String(source.rawMax ?? source.raw_max ?? "32767"),
    engMin: String(source.engMin ?? source.eng_min ?? "0"),
    engMax: String(source.engMax ?? source.eng_max ?? "100"),
    unit: source.unit || "",
    inputType: Number(source.inputType ?? source.input_type ?? 10),
    unitIndex: Number(source.unitIndex ?? source.unit_index ?? 1),
  });
  const [form, setForm] = useState<ScalingForm>(() => normalizeTagForm(tag));

  const [indicators, setIndicators] = useState<any[]>([]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(`level_indicators_${tag.id}`);
      if (stored) setIndicators(JSON.parse(stored));
      else setIndicators([{ id: "1", value: 100, color: "#ef4444" }]);
    } catch (e) {}
  }, [tag.id]);

  useEffect(() => {
    if (showConfig) return;
    setForm(normalizeTagForm(tag));
  }, [tag.scaling, tag.rawMin, tag.raw_min, tag.rawMax, tag.raw_max, tag.engMin, tag.eng_min, tag.engMax, tag.eng_max, tag.unit, tag.inputType, tag.input_type, tag.unitIndex, tag.unit_index]);

  const dk = theme === "dark";
  const hasValue = tag.value !== undefined && tag.value !== null;
  const displayValue = (() => {
    const rawValue = Number(tag.value);
    const rawMin = Number(tag.rawMin ?? tag.raw_min);
    const rawMax = Number(tag.rawMax ?? tag.raw_max);
    const engMin = Number(tag.engMin ?? tag.eng_min);
    const engMax = Number(tag.engMax ?? tag.eng_max);
    const scalingEnabled = tag.scaling === true || tag.scaling === 1;
    if (!hasValue || !scalingEnabled || ![rawValue, rawMin, rawMax, engMin, engMax].every(Number.isFinite) || rawMax === rawMin) {
      return tag.value;
    }
    return engMin + ((rawValue - rawMin) * (engMax - engMin)) / (rawMax - rawMin);
  })();
  const isChannelSensor = Number(tag.address) >= 3 && Number(tag.address) <= 18;
  let pct = 0;

  if (isChannelSensor && hasValue) {
    const val = Number(displayValue);
    const engMin = Number(tag.engMin ?? tag.eng_min ?? 0);
    const engMax = Number(tag.engMax ?? tag.eng_max ?? 100);
    pct = engMax !== engMin ? ((val - engMin) / (engMax - engMin)) * 100 : val;
    pct = Math.max(0, Math.min(100, pct));
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      const engMin = Number(form.engMin);
      const engMax = Number(form.engMax);
      const selectedUnitObj = UNIT_TYPES.find(u => u.value === form.unitIndex);
      const resolvedUnit = selectedUnitObj ? selectedUnitObj.label : form.unit;

      const payload: any = {
        scaling: form.scaling,
        rawMin: parseFloat(form.rawMin),
        rawMax: parseFloat(form.rawMax),
        engMin,
        engMax,
        unit: resolvedUnit,
        input_type: form.inputType,
        unit_index: form.unitIndex,
      };

      const res = await fetch(`${API_BASE}/api/admin/scanners/${plcId}/tags/${tag.id}`, {
        method: "PUT",
        headers: getAuthHeaders(token, { "Content-Type": "application/json" }),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        toast.error(await readApiError(res, "Failed to save"));
        return;
      }
      if (tag.address >= 3 && tag.address <= 18) {
        const channel = tag.address - 2;
        // One combined write (scaling + input type + unit) instead of three separate
        // round-trips — faster and avoids the poll loop interleaving between writes.
        const writeRes = await fetch(`${API_BASE}/api/admin/scanners/${plcId}/channel/${channel}/full-config`, {
          method: "PUT",
          headers: getAuthHeaders(token, { "Content-Type": "application/json" }),
          body: JSON.stringify({ min: engMin, max: engMax, inputType: form.inputType, unitIndex: form.unitIndex }),
        });
        if (!writeRes.ok) {
          toast.error(await readApiError(writeRes, "Saved settings, but failed to write configuration to device"));
          return;
        }
      }
      toast.success("Saved");
      setShowConfig(false);
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || "Error");
    } finally { setSaving(false); }
  };

  return (
    <motion.div layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full select-none">
      <Card className={`overflow-hidden border transition-all duration-300 ${hasValue ? (dk ? "border-green-500/50" : "border-green-400") : ""}`}>
        <div className="px-3 pt-2 pb-0 flex items-start justify-between">
          <div>
            <h3 className="font-bold text-xl truncate">{tag.name}</h3>
            <p className="text-sm text-slate-500">{getChannelName(tag.address)}</p>
          </div>
          {userRole !== "operator" && (
            <button onClick={() => setShowConfig(!showConfig)} className={`p-1.5 rounded-lg ${showConfig ? "bg-blue-500 text-white" : "hover:bg-slate-100"}`}>
              <Settings2 className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="px-3 pb-2 flex-grow min-h-[80px]">
          <div className="text-4xl font-bold">
            {hasValue ? (
              typeof displayValue === "number" ? <AnimatedNumber value={displayValue} decimals={tag.scaling ? 2 : 1} />
              : typeof displayValue === "boolean" ? (displayValue ? "1" : "0")
              : displayValue
            ) : "-"}
            {hasValue && tag.unit ? <span className="ml-1 text-base text-slate-500">{tag.unit}</span> : null}
          </div>
          {isChannelSensor && (
            <IndicatorCirclesRow
              indicators={indicators}
              pct={pct}
              hasValue={hasValue}
              dk={dk}
              tagId={tag.id}
              showSettings={showConfig && userRole !== "operator"}
              onIndicatorsChange={(next: any) => { setIndicators(next); localStorage.setItem(`level_indicators_${tag.id}`, JSON.stringify(next)); }}
            />
          )}
          {history.length > 1 && (
            <div className="h-14 mt-2 -mx-1">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={history} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                  <defs>
                    <linearGradient id={`grad-${tag.id}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <YAxis domain={['auto', 'auto']} hide />
                  <Tooltip
                    contentStyle={{ background: 'rgba(15,23,42,0.85)', border: 'none', borderRadius: 6, fontSize: 11, color: '#e2e8f0' }}
                    formatter={(v: any) => [typeof v === 'number' ? v.toFixed(2) : v, tag.unit || tag.name]}
                    labelFormatter={(l) => String(l)}
                  />
                  <Area type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={1.5} fill={`url(#grad-${tag.id})`} dot={false} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <AnimatePresence>
          {showConfig && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className={`border-t ${dk ? "border-slate-700 bg-slate-900/50" : "border-blue-100 bg-blue-50/60"} px-5 py-4`}
            >
              <p className="text-sm font-semibold mb-3 text-blue-500 flex items-center gap-2">
                <Settings2 className="w-4 h-4" /> Signal Scaling
              </p>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className={`text-sm font-semibold ${dk ? "text-blue-300" : "text-blue-600"}`}>Min Value</Label>
                  <Input
                    className={`h-9 text-sm font-medium ${dk ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-blue-300 text-slate-900"}`}
                    type="number" step="any" value={form.engMin}
                    onChange={(e) => setForm({ ...form, engMin: e.target.value })} placeholder="e.g. -50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={`text-sm font-semibold ${dk ? "text-blue-300" : "text-blue-600"}`}>Max Value</Label>
                  <Input
                    className={`h-9 text-sm font-medium ${dk ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-blue-300 text-slate-900"}`}
                    type="number" step="any" value={form.engMax}
                    onChange={(e) => setForm({ ...form, engMax: e.target.value })} placeholder="e.g. 100"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className={`text-sm font-semibold ${dk ? "text-blue-300" : "text-blue-600"}`}>Input Type</Label>
                  <select
                    className={`w-full h-9 px-3 text-sm font-medium rounded-md border ${dk ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-blue-300 text-slate-900"}`}
                    value={form.inputType} onChange={(e) => setForm({ ...form, inputType: parseInt(e.target.value) })}
                  >
                    {INPUT_TYPES.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className={`text-sm font-semibold ${dk ? "text-blue-300" : "text-blue-600"}`}>Unit</Label>
                  <select
                    className={`w-full h-9 px-3 text-sm font-medium rounded-md border ${dk ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-blue-300 text-slate-900"}`}
                    value={form.unitIndex}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      const label = UNIT_TYPES.find(u => u.value === val)?.label || "";
                      setForm({ ...form, unitIndex: val, unit: label });
                    }}
                  >
                    {UNIT_TYPES.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white flex-1">
                  <Check className="w-3.5 h-3.5 mr-1.5" />{saving ? "Saving..." : "Save"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowConfig(false)} className="flex-1">
                  <X className="w-3.5 h-3.5 mr-1.5" />Cancel
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </motion.div>
  );
}

export function ScannerDashboard({ clientId, token, permissions, pollingIntervalMs = 5000 }: ScannerDashboardProps) {
  const { theme } = useTheme();
  const dk = theme === "dark";

  const [scanners, setScanners] = useState<any[]>([]);
  const [readings, setReadings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedScannerId, setSelectedScannerId] = useState<string | null>(null);
  // Last-seen timestamp per scanner (ms epoch) — tracked for every scanner, not just
  // whichever one is currently selected, so the picker below can show accurate
  // online/offline for all of them at once.
  const [scannerLastSeen, setScannerLastSeen] = useState<Record<string, number>>({});
  const SCANNER_ONLINE_WINDOW_MS = Math.max(30000, pollingIntervalMs * 3);
  const [, forceStatusTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => forceStatusTick((n) => n + 1), 10000);
    return () => clearInterval(interval);
  }, []);
  const isScannerOnline = useCallback(
    (scannerId: string) => {
      const lastSeen = scannerLastSeen[scannerId];
      return !!lastSeen && (Date.now() - lastSeen) < SCANNER_ONLINE_WINDOW_MS;
    },
    [scannerLastSeen, SCANNER_ONLINE_WINDOW_MS]
  );

  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const [scannersRes, readingsRes] = await Promise.all([
        fetch(`${API_BASE}/api/clients/${clientId}/scanners`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/api/clients/${clientId}/scanners/readings?limit=200`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const scannersData = await scannersRes.json();
      const readingsData = await readingsRes.json();

      if (scannersRes.ok) {
        const nextScanners = scannersData.scanners || [];
        setScanners(nextScanners);
        setSelectedScannerId((prev) => {
          if (prev && nextScanners.some((s: any) => s.id === prev)) return prev;
          return nextScanners[0]?.id || null;
        });
      }
      if (readingsRes.ok) {
        const nextReadings = readingsData.readings || [];
        setReadings(nextReadings);
        setScannerLastSeen((prev) => {
          const next = { ...prev };
          for (const r of nextReadings) {
            const scannerId = r.scanner_id;
            const ts = new Date(r.reading_timestamp).getTime();
            if (!scannerId || !Number.isFinite(ts)) continue;
            if (!next[scannerId] || ts > next[scannerId]) next[scannerId] = ts;
          }
          return next;
        });
      }

    } catch (err: any) {
      console.error('Failed to load scanner data:', err);
      toast.error('Failed to load scanner data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [clientId, token]);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => loadData(true), pollingIntervalMs);
    return () => clearInterval(interval);
  }, [loadData, pollingIntervalMs]);

  const currentPLC = scanners.find((s) => s.id === selectedScannerId) || scanners[0] || null;
  const [viewMode, setViewMode] = useState<"cards" | "tanks">("cards");
  const [historyData, setHistoryData] = useState<Record<string, any[]>>({});
  const lastCommunicationToastAtRef = useRef<Record<string, number>>({});
  const [liveReadings, setLiveReadings] = useState<any[]>([]);

  // Switching scanners should drop stale live values from whichever scanner was
  // previously selected — the REST `readings` fallback in tagsWithValues still
  // covers the gap until fresh live data for the new selection arrives.
  useEffect(() => {
    setLiveReadings([]);
  }, [selectedScannerId]);

  useEffect(() => {
    let isMounted = true;
    const ws = getWebSocketClient(token, { clientId, pollingIntervalMs });

    const handleLiveData = (data: any) => {
      if (!isMounted) return;
      if (data.type === 'plc_live_data' && data.connectionType === 'scanner') {
        if (data.plcId) {
          setScannerLastSeen((prev) => ({ ...prev, [data.plcId]: Date.now() }));
        }
        if (currentPLC && data.plcId !== currentPLC.id) return;
        setLiveReadings(prev => {
          const next = [...prev];
          for (const [name, value] of Object.entries(data.values || {})) {
            const existingIdx = next.findIndex(r => r.tag_name === name);
            if (existingIdx !== -1) {
              next[existingIdx] = { ...next[existingIdx], value };
            } else {
              next.push({ tag_name: name, value });
            }
          }
          return next;
        });
      }
    };

    ws.on('plc_live_data', handleLiveData);
    // Registering a handler alone doesn't establish a connection — if no other
    // screen has connected the shared client yet, live data never arrives.
    ws.connect();
    return () => {
      isMounted = false;
      ws.off('plc_live_data', handleLiveData);
    };
  }, [clientId, token, pollingIntervalMs, currentPLC]);

  // Map latest reading to each tag
  const tagsWithValues = useMemo(() => currentPLC ? currentPLC.tags.map((tag: any) => {
    const liveMatch = liveReadings.find(r => r.tag_name === tag.name);
    if (liveMatch && liveMatch.value !== undefined && liveMatch.value !== null) {
      return { ...tag, value: liveMatch.value };
    }
    const reading = readings.find(r => r.tag_id === tag.id || r.tag_name === tag.name);
    return { ...tag, value: reading ? reading.value : null };
  }) : [], [currentPLC, liveReadings, readings]);

  const currentPLCTags = tagsWithValues;
  const plcId = currentPLC?.id;

  const userRole = permissions?.role || 'admin';
  const visibleTags = permissions?.visibleTags || []; 

  // Accumulate history data
  useEffect(() => {
    if (!currentPLC || currentPLCTags.length === 0) return;
    
    const hasAnyLiveValue = currentPLCTags.some((tag: any) => tag.value !== undefined && tag.value !== null);
    if (!hasAnyLiveValue && currentPLCTags.length > 0) {
      const nowMs = Date.now();
      const lastToastAt = lastCommunicationToastAtRef.current[plcId] || 0;
      if (nowMs - lastToastAt >= 5000) {
        toast.error("Communication error");
        lastCommunicationToastAtRef.current[plcId] = nowMs;
      }
    } else {
      lastCommunicationToastAtRef.current[plcId] = 0;
    }

    if (!hasAnyLiveValue) {
      setHistoryData(prev => {
        const next = { ...prev };
        let changed = false;
        currentPLCTags.forEach((tag: any) => {
          if (next[tag.id]?.length) {
            next[tag.id] = [];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
      return;
    }

    const now = new Date().toLocaleTimeString();
    setHistoryData(prev => {
      const newData = { ...prev };
      let hasChanges = false;

      currentPLCTags.forEach((tag: any) => {
        if (tag.value !== undefined && tag.value !== null) {
          if (!newData[tag.id]) {
            newData[tag.id] = [];
          }

          // Boolean tags (M-bit/coil) need coercing to 0/1 — mixing true/false into
          // a numeric sparkline's data array breaks the chart's Y-axis domain math.
          const pointValue = typeof tag.value === 'boolean' ? (tag.value ? 1 : 0) : tag.value;
          const newPoint = { time: now, value: pointValue };
          const lastPoint = newData[tag.id][newData[tag.id].length - 1];

          // Avoid duplicate timestamps
          if (!lastPoint || lastPoint.time !== now) {
            newData[tag.id] = [...newData[tag.id], newPoint].slice(-30); // Keep last 30 points
            hasChanges = true;
          }
        }
      });

      return hasChanges ? newData : prev;
    });
  }, [currentPLC, currentPLCTags, plcId]);

  if (!currentPLC) {
    return (
      <div className={`p-8 ${dk ? "text-white" : ""}`}>
        <div className="text-center py-20 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-3xl">
          <Activity className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className={`text-xl font-bold mb-2 ${dk ? "text-slate-200" : "text-slate-800"}`}>No Scanner Selected</h3>
          <p className={`${dk ? "text-slate-400" : "text-gray-500"}`}>Add a scanner in the Admin Panel to start monitoring</p>
        </div>
      </div>
    );
  }

  const allTags = currentPLCTags || [];
  const totalTags = allTags.length;

  // Filter tags for graphs based on user permissions
  const filteredGraphTags = (userRole === "admin" && visibleTags.length === 0)
    ? allTags
    : allTags.filter((tag: any) => visibleTags.includes(tag.id));

  return (
    <div className={`p-8 ${dk ? "text-white" : ""}`}>
      {/* Scanner Selector — which scanner (if there's more than one) to view, with online/offline status */}
      {scanners.length > 0 && (
        <div className={`rounded-lg p-4 mb-4 border ${dk ? "bg-slate-900/70 border-slate-700" : "bg-white border-slate-200"}`}>
          <div className="flex flex-wrap gap-3">
            {scanners.map((scanner) => {
              const isSelected = scanner.id === currentPLC?.id;
              const online = isScannerOnline(scanner.id);
              return (
                <button
                  key={scanner.id}
                  onClick={() => setSelectedScannerId(scanner.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    isSelected
                      ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg"
                      : dk
                        ? "bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200"
                  }`}
                >
                  {online ? <Wifi className="h-3.5 w-3.5 text-emerald-400" /> : <WifiOff className="h-3.5 w-3.5 text-red-400" />}
                  <span>{scanner.name}</span>
                  <span className={`w-1.5 h-1.5 rounded-full ${online ? "bg-emerald-400" : "bg-red-400"}`} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* View Toggle */}
      {totalTags > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-end mb-4"
        >
          <div className={`flex p-1 rounded-lg transition-all duration-300 ${dk
              ? "bg-slate-800/50 border border-slate-700/50"
              : "bg-blue-100/50 border border-blue-200 shadow-sm"
            }`}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setViewMode("cards")}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-300 ${viewMode === "cards"
                ? dk
                  ? "bg-slate-700 text-white shadow-lg"
                  : "bg-white text-blue-600 shadow-md font-semibold"
                : dk
                  ? "text-slate-400 hover:text-slate-200"
                  : "text-blue-600 hover:text-blue-700"
                }`}
            >
              Cards
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setViewMode("tanks")}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-300 ${viewMode === "tanks"
                ? dk
                  ? "bg-slate-700 text-white shadow-lg"
                  : "bg-white text-blue-600 shadow-md font-semibold"
                : dk
                  ? "text-slate-400 hover:text-slate-200"
                  : "text-blue-600 hover:text-blue-700"
                }`}
            >
              Tanks
            </motion.button>
          </div>
        </motion.div>
      )}

      {/* Content Area */}
      {viewMode === "cards" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
          {(() => {
            const rows = 4;
            const cols = 4;
            const reordered = new Array(rows * cols).fill(null);
            
            // Reorder tags to flow column-wise in a standard row-wise CSS grid
            allTags.slice(0, 16).forEach((tag: any, i: number) => {
              const col = Math.floor(i / rows);
              const row = i % rows;
              if (row < rows && col < cols) {
                reordered[row * cols + col] = tag;
              }
            });

            return reordered.map((tag, idx) => (
              <div key={tag?.id || `empty-${idx}`} className={tag ? "w-full" : "hidden lg:block opacity-0 pointer-events-none"}>
                {tag && (
                  <TagCard
                    tag={tag}
                    plcId={currentPLC.id}
                    onRefresh={() => loadData(true)}
                    theme={theme}
                    userRole={userRole}
                    token={token}
                    history={historyData[tag.id] || []}
                  />
                )}
              </div>
            ));
          })()}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredGraphTags.length > 0 ? (
            filteredGraphTags.map((tag: any) => (
              <motion.div
                key={tag.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -4 }}
                className="w-full"
              >
                <CylinderMimic tag={tag} userRole={userRole} />
              </motion.div>
            ))
          ) : (
            <div className="col-span-full text-center py-20 bg-slate-50 dark:bg-slate-800/20 rounded-3xl border border-dashed border-slate-200 dark:border-slate-700">
              <Activity className="w-12 h-12 mx-auto mb-4 text-slate-500" />
              <h3 className="text-lg font-bold">No Tanks Enabled</h3>
              <p className="text-slate-500">An administrator needs to enable tag visibility for your account in User Management.</p>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {totalTags === 0 && (
        <div className="text-center py-20 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-3xl mt-6">
          <TrendingDown className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className={`text-xl font-bold mb-2 ${dk ? "text-slate-200" : "text-slate-800"}`}>No Tags Configured</h3>
          <p className={`${dk ? "text-slate-400" : "text-gray-500"}`}>Go to <strong>Admin Panel</strong> → <strong>"Manage Tags"</strong></p>
        </div>
      )}
    </div>
  );
}
